import { Stack, Typography, CircularProgress, Alert } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { usePageTitle } from "../../../../hooks/usePageTitle";
import { useParams } from "react-router-dom";
import { HalftimeDashboardRow } from "./types";
import Legend from "./Legend";
import AdvancedBoxScoreTable from "./AdvancedBoxScoreTable";
import { ExplorerAPI } from "../../../../util/Requests";
import { ExplorerEnvironment } from "../../../../data/Cache";
import { NBA_TEAMS } from "../../../../data/teams";
import { getTeamLogoUrl } from "../../../../models/Team";

export default function HalftimeDashboard() {
  const { gameId } = useParams<{ gameId: string }>();
  const [rows, setRows] = useState<HalftimeDashboardRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const teams = useMemo(() => {
    return rows
      .filter((row) => row.isTeam)
      .map((row) => {
        const nbaTeam = NBA_TEAMS.find(
          (t) => `${t.city} ${t.name}` === row.name,
        );
        return {
          id: row.id,
          abbrev: nbaTeam?.abbrev ?? row.name,
          logoUrl: nbaTeam ? getTeamLogoUrl(nbaTeam) : "",
        };
      });
  }, [rows]);

  const getTeamRows = (teamId: string): HalftimeDashboardRow[] => {
    return rows.filter(
      (row) =>
        row.teamId === teamId &&
        (row.isTeam || (row.totalPossessions ?? 0) >= 1),
    );
  };

  const fetchData = useCallback(() => {
    if (!gameId) return;
    const api = new ExplorerAPI(ExplorerEnvironment.NBA);
    api
      .getHalftimeDashboardData(gameId)
      .then((data) => {
        setRows(data);
        setLoading(false);
        setError(null);
      })
      .catch((err) => {
        console.error("Error fetching halftime data:", err);
        setError("Failed to load data.");
        setLoading(false);
      });
  }, [gameId]);

  usePageTitle("Halftime Dashboard - Explorer");

  useEffect(() => {
    if (!gameId) {
      setLoading(false);
      setError("No game ID provided");
      return;
    }

    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [gameId, fetchData]);

  if (loading) {
    return (
      <Stack sx={{ p: 4, alignItems: "center" }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading halftime data...</Typography>
      </Stack>
    );
  }

  if (error) {
    return (
      <Stack sx={{ p: 4, alignItems: "center" }}>
        <Alert severity="error" sx={{ maxWidth: 600 }}>
          {error}
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack sx={{ p: { xs: 2, md: 4 }, alignItems: "center" }}>
      <Typography variant="h3" sx={{ mb: 3 }}>
        Halftime Dashboard
      </Typography>

      <Stack
        direction="row"
        spacing={4}
        sx={{
          width: "100%",
          minWidth: 0,
          justifyContent: { md: "center" },
          overflow: "auto",
        }}
      >
        {teams.map((team) => (
          <AdvancedBoxScoreTable
            key={team.id}
            rows={getTeamRows(team.id)}
            teamAbbrev={team.abbrev}
            logoUrl={team.logoUrl}
          />
        ))}
      </Stack>

      <Legend />
    </Stack>
  );
}
